import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DetectMethodChanges } from '../../utils';
import {
    DisabledDatesArg,
    makeExpirationDisabledDates,
    makeRangeBoundaryDisabledDates,
    nextSelectableDayAfter
} from '../../copilot-shared/expiration-dates';
import { ExclusionCalendarResult } from './apg-exclusion-calendar.component';
import { BiasDirection, RollPositionsInputs, RollTabState, WEEKDAYS, Weekday } from './apg-copilot-domain';
import {
    emptyRollInputs,
    exceedsRangeCap,
    isRollComplete,
    rangeCapMessage,
    selectRange,
    selectSingleDate,
    setBiasDirection,
    setBiasEnabled,
    toggleWeekdayExclusion,
    weekdayOverrideKeys
} from './apg-copilot-questionnaire';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW3 = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// The Roll Positions questionnaire. Holds RollPositionsInputs as the single source of truth and
// drives every mutation through the pure reducers (apg-copilot-questionnaire), so the UI can never
// diverge from the tested rules. Emits {inputs, valid} on every edit.
@Component({
    selector: 'ets-apg-roll-positions-panel',
    templateUrl: './apg-roll-positions-panel.component.html',
    styleUrls: ['./apg-roll-positions-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApgRollPositionsPanelComponent implements OnInit {

    constructor(private readonly _changeDetector: ChangeDetectorRef) {}

    @Output() stateChange = new EventEmitter<RollTabState>();

    // Layer 2: chain-listed expirations ("yyyy-MM-dd") — the roll-date candidate universe. Passed to
    // the exclusion calendar (only listed expirations toggleable) AND used to restrict the single/range
    // date-boxes to tradeable dates via disabledDatesFn, rebuilt on every list change so the widget
    // re-applies the option. Empty = chain not loaded yet -> weekend-only fallback (see the shared helper).
    @Input()
    set availableExpirations(v: string[]) {
        this._availableExpirations = v || [];
        this.disabledDatesFn = makeExpirationDisabledDates(this._availableExpirations);
        // The range predicate is built from this list TOO, so it must be rebuilt here as well.
        // Cause -> effect: the signature guard inside rebuildRangeDisabledDates only watches the
        // weekday-rule state, so without this invalidation a chain arriving after the first build
        // would leave the boundaries gated by the weekend-only fallback for the rest of the session.
        this._rangeDisabledSignature = null;
        this.rebuildRangeDisabledDates();
        // The chain arriving AFTER the range tab was presented: a seed placed under the weekend-only
        // fallback may sit on a non-expiration day. Re-seed — but ONLY a seed. A trader-picked Start
        // is never overwritten (preselectStart refuses when the value is the trader's). A moved seed
        // is a state change the host must hear (its rollInputs feed the Run) — emit, once initialised.
        const moved = this.preselectStart() || this.preselectSingle();
        if (moved && this._initialised) {
            this.emit();
        }
    }
    get availableExpirations(): string[] {
        return this._availableExpirations;
    }
    private _availableExpirations: string[] = [];

    // Owner ruling 2026-09-08 (screencast 16-59-17): the Start ANCHOR reader — the earlier ShortOption
    // expiration of the portfolio, or the authoritative "today" when that is past, as a "yyyy-MM-dd"
    // key (IApgCopilotFacade.readRollAnchorKey). A READER, not a value: the shorts are edited while
    // ORION is open and the QG cursor moves. Read only at the explicit moments below (never per
    // change-detection pass — in a QG shell one read is a bus round-trip). Null = no ShortOption leg
    // or no expiration selected -> no seed and no block (Start stays empty; the trader picks).
    @Input() anchorKeyReader: (() => string | null) | null = null;

    // CR-20260916-005 (Maven, owner ruling 2026-09-16: "clamp to today"): when true, the SINGLE
    // date follows the anchor the way the range Start does — [min] = the anchor day, and an empty
    // single date is SEEDED with the first selectable listed day on or after it (so the calendar
    // opens on the replay's day, not the browser's). Off by default: the APG ORION's single date
    // keeps its free pick. A seed may be re-placed; a trader's pick never.
    @Input() singleDateFollowsAnchor = false;
    private _singleIsSeed = false;

    // The anchor as last read (refreshAnchor). Drives the Start picker's [min] and the seed.
    private _anchorKey: string | null = null;

    // True while the current range Start is a SEED (placed by preselectStart), false once the trader
    // has picked one. Guards the re-seed on chain arrival: a seed may be moved, a pick never.
    private _startIsSeed = false;

    // SWITCHING MODES NEVER RESETS A PICKED VALUE (owner ruling 2026-09-15, twice: "I switch back
    // to the date range and I lose the end date", then "now Single Date loses its value when
    // switching into different mode"). The reducers hold ONE date selection at a time —
    // selectSingleDate drops both boundaries, selectRange drops the single date — so the value
    // the trader leaves behind is PARKED here and restored on the way back: the range with
    // whether its Start was the trader's own (never re-seeded on return), the single date as is.
    // Cleared by resetInputs, and each one once consumed.
    // Maven preserves the range's exclusions; APG retains its existing clearing/default rule.
    @Input() rememberRangeExclusions = false;
    private _parkedRange: {
        start: Date | null; end: Date | null; startIsSeed: boolean;
        weekdayExclusions: Weekday[]; specificDateExclusions: Date[]; weekdayOverrides: Date[];
    } | null = null;
    private _parkedSingle: Date | null = null;

    // True while the option chain is still resolving -> the Dates section shows a loading indicator
    // and the date-boxes disable weekends only (fallback) until the full expiration list arrives.
    @Input() expirationsLoading = false;

    // CR-20260831-004: the fields that no longer match what the CURRENT run sent, diffed by the
    // orchestrator (apg-copilot-param-diff key grammar). A FRESH object per recompute is the OnPush
    // wake-up. Null/empty = clean (or no run yet) — every cue off.
    @Input() changedKeys: { [key: string]: boolean } | null = null;

    // CR-20260904-001 (BR-W1 / REQ-4): the widest range a trader may ask for, in whole days from
    // the START date. Since the 2026-09-08 ruling it is the shell-level "Max Query Range (days)"
    // ORION setting and binds EVERYONE; null = unlimited only while the setting is unknown.
    // Supplied by the orchestrator from OrionSettingsService.
    @Input()
    set maxRangeDays(v: number | null) {
        const next = (typeof v === 'number' && isFinite(v)) ? v : null;
        if (next === this._maxRangeDays) {
            return;
        }
        this._maxRangeDays = next;
        // Cause -> effect: if the limit ever arrives AFTER the trader has already picked a wide
        // range, rollValid would stay stale and Run would remain enabled over an illegal range.
        // Only re-emit once initialised — at creation ngOnInit's own emit already carries the
        // value, so the normal path cannot raise ExpressionChangedAfterItHasBeenChecked.
        if (this._initialised) {
            this.emit();
        }
    }
    get maxRangeDays(): number | null {
        return this._maxRangeDays;
    }
    private _maxRangeDays: number | null = null;
    private _initialised = false;

    // CR-20260915-002: the host's "Weekdays to exclude" setting - the STARTING weekday-exclusion
    // set of a range (the APG ORION's is Monday by default, Maven's is none). Read at the three
    // seeding moments only: first presentation, resetInputs, and entering range mode. It never
    // rewrites a set the trader is already editing. Null = the compiled fallback (Monday).
    @Input()
    set defaultWeekdayExclusions(v: Weekday[] | null) {
        this._defaultWeekdayExclusions = v || null;
    }
    get defaultWeekdayExclusions(): Weekday[] | null {
        return this._defaultWeekdayExclusions;
    }
    private _defaultWeekdayExclusions: Weekday[] | null = null;

    // Template guard for the per-control [class.param-changed] bindings.
    isChanged(key: string): boolean {
        return !!(this.changedKeys && this.changedKeys[key]);
    }

    // SINGLE-DATE [disabledDates] predicate: only listed expirations are clickable (weekends +
    // non-expiration days disabled). Seeded to the pre-load fallback (weekends only). Deliberately
    // free of the weekday rule — exclusions do not apply to a single date (BR-W2), so gating an
    // excluded weekday here would tell the trader the opposite of the rule.
    disabledDatesFn: (arg: DisabledDatesArg) => boolean = makeExpirationDisabledDates([]);

    // CR-20260904-001 (BR-W5): the START/END [disabledDates] predicate — the tradeable rule PLUS the
    // WEEKDAY rule (every excluded weekday since CR-20260915-002). An excluded day is NOT a legal
    // boundary; bringing it back is the exclusion calendar's job and nowhere else's, so the
    // boundary pickers no longer edit exclusions as a side effect of being used. ONE shared
    // function is fine (unlike the old calendarOptions objects, which DevExtreme mutated per box):
    // a predicate is stateless and is only ever called.
    rangeDisabledDatesFn: (arg: DisabledDatesArg) => boolean = makeExpirationDisabledDates([]);
    private _rangeDisabledSignature: string | null = null;

    inputs: RollPositionsInputs = emptyRollInputs();
    calendarOpen = false;

    readonly weekdays: Weekday[] = WEEKDAYS;
    readonly biasDirections: { id: BiasDirection; text: string }[] = [
        { id: 'up', text: 'Up' },
        { id: 'down', text: 'Down' },
        { id: 'sideways', text: 'Sideways' },
        { id: 'auto', text: 'Auto' }
    ];

    // dx-button-group item sets (same segmented control the Hedge CoPilot uses for its toggles).
    readonly dateKindButtons: { id: 'single' | 'range'; text: string }[] = [
        { id: 'single', text: 'Single Date' },
        { id: 'range', text: 'Date Range' }
    ];
    readonly biasButtons: { id: 'yes' | 'no'; text: string }[] = [
        { id: 'yes', text: 'Yes' },
        { id: 'no', text: 'No' }
    ];
    readonly weekdayButtons: { id: Weekday; text: string }[] =
        WEEKDAYS.map(wd => ({ id: wd, text: wd }));

    // Multi-select group is a controlled binding: state is the source of truth, so the selected keys are
    // always re-derived from the model (mirrors HG's one-way [selectedItemKeys] + (onItemClick) pattern).
    get selectedWeekdayKeys(): Weekday[] {
        return this.inputs.weekdayExclusions;
    }

    ngOnInit(): void {
        this._initialised = true;
        // The inputs are untouched before ngOnInit (nothing binds them), so this is the first
        // seeding moment: the field initialiser above ran before [defaultWeekdayExclusions] was
        // bound and holds the compiled fallback.
        this.inputs = emptyRollInputs(this._defaultWeekdayExclusions);
        // Presented already in range mode with no Start (e.g. restored state) -> seed now. The
        // trigger is the EMPTY START on presentation, not the kind-switch gesture (owner, 2026-09-08).
        this.preselectStart();
        this.preselectSingle();
        this.emit();
    }

    // Public reset (called by the orchestrator on portfolio switch via a content re-create).
    @DetectMethodChanges()
    restoreInputs(inputs: RollPositionsInputs): void {
        this.inputs = inputs;
        this._startIsSeed = false;
        this._singleIsSeed = false;
        this._parkedRange = null;
        this._parkedSingle = null;
        this.calendarOpen = false;
        this.refreshAnchor();
        this.emit();
    }

    resetInputs(): void {
        this.inputs = emptyRollInputs(this._defaultWeekdayExclusions);
        this._startIsSeed = false;
        this._singleIsSeed = false;
        this._parkedRange = null;
        this._parkedSingle = null;
        this._anchorKey = null;
        this.calendarOpen = false;
        this.emit();
    }

    // CR-20260916-005: the single date's [min] — the anchor day, only when the host asked the
    // single date to follow the anchor. Memoised on the anchor key (DevExtreme re-applies [min]
    // on every fresh Date, see rangeEndMax).
    get singleDateMin(): Date | undefined {
        if (!this.singleDateFollowsAnchor) {
            return undefined;
        }
        return this.rangeStartMin;
    }

    // ---- getters used by the template -------------------------------------------------

    get isSingle(): boolean {
        return this.inputs.date.kind === 'single';
    }

    get singleDate(): Date | null {
        return this.inputs.date.kind === 'single' ? this.inputs.date.single : null;
    }

    get rangeStart(): Date | null {
        return this.inputs.date.kind === 'range' ? this.inputs.date.start : null;
    }

    get rangeEnd(): Date | null {
        return this.inputs.date.kind === 'range' ? this.inputs.date.end : null;
    }

    // Owner ruling 2026-09-08: the Start picker refuses every day BEFORE the anchor (the short-option
    // expiration); the anchor day itself stays selectable, so [min] = the anchor day (dx min is
    // inclusive). Undefined = no anchor = no block. Memoised on the anchor key for the same DevExtreme
    // reason as rangeEndMax below.
    get rangeStartMin(): Date | undefined {
        if (this._anchorKey !== this._rangeStartMinKey) {
            this._rangeStartMinKey = this._anchorKey;
            this._rangeStartMin = this._anchorKey ? this.keyToLocalDate(this._anchorKey) : undefined;
        }
        return this._rangeStartMin;
    }
    private _rangeStartMinKey: string | null = null;
    private _rangeStartMin: Date | undefined = undefined;

    // CR-20260904-001 follow-on (2026-09-08): the End picker offers nothing beyond start + cap, so
    // the trader sees how far out he may go instead of discovering it from the refusal message.
    // Undefined (no bound) until BOTH a start date and the setting are known. The message and the
    // Run gate stay as the second guard: a range entered another way is still refused.
    // Memoised on (start, cap): handing [max] a fresh Date every change-detection pass would make
    // DevExtreme re-apply the option mid-interaction, the same reset the disabledDates predicate
    // guards against below.
    get rangeEndMax(): Date | undefined {
        const start = this.rangeStart;
        if (!start || this._maxRangeDays == null) {
            this._rangeEndMaxKey = null;
            this._rangeEndMax = undefined;
            return undefined;
        }
        const key = start.getFullYear() + '-' + start.getMonth() + '-' + start.getDate() + '/' + this._maxRangeDays;
        if (key !== this._rangeEndMaxKey) {
            const max = new Date(start.getFullYear(), start.getMonth(), start.getDate());
            max.setDate(max.getDate() + this._maxRangeDays);
            this._rangeEndMaxKey = key;
            this._rangeEndMax = max;
        }
        return this._rangeEndMax;
    }
    private _rangeEndMaxKey: string | null = null;
    private _rangeEndMax: Date | undefined = undefined;

    // Owner request 2026-09-08: the End picker is FORWARD-ONLY. [min] = start + 1 day, so every day
    // up to and INCLUDING the Start is crossed out in the End dropdown. Undefined (no bound) while
    // there is no Start — but then the End box is disabled anyway (see the template), so the bound
    // is never observable without a Start. Memoised on the start day for the same DevExtreme reason
    // as rangeEndMax above.
    get rangeEndMin(): Date | undefined {
        const start = this.rangeStart;
        if (!this.isValid(start)) {
            this._rangeEndMinKey = null;
            this._rangeEndMin = undefined;
            return undefined;
        }
        const s = start as Date;
        const key = s.getFullYear() + '-' + s.getMonth() + '-' + s.getDate();
        if (key !== this._rangeEndMinKey) {
            this._rangeEndMinKey = key;
            this._rangeEndMin = new Date(s.getFullYear(), s.getMonth(), s.getDate() + 1);
        }
        return this._rangeEndMin;
    }
    private _rangeEndMinKey: string | null = null;
    private _rangeEndMin: Date | undefined = undefined;

    // The End box is dead until a Start exists: the End rule is "after the Start", and without a
    // Start there is nothing to be after.
    get rangeEndDisabled(): boolean {
        return !this.isValid(this.rangeStart);
    }

    get biasEnabled(): boolean {
        return this.inputs.bias.enabled;
    }

    get biasDir(): BiasDirection | null {
        return this.inputs.bias.direction;
    }

    // Committed specific-date exclusions, rendered as read-only pills in the collapsed trigger, in the
    // unified EEE dd-MMM-yy format ("Wed 08-Jul-26"), sorted ascending (invalid dates skipped). These are
    // display-only here — the user edits them by clicking the trigger to re-open the calendar (owner ruling).
    get excludedDatePills(): string[] {
        return this.inputs.specificDateExclusions
            .filter(d => this.isValid(d))
            .sort((a, b) => a.getTime() - b.getTime())
            .map(d => this.fmtDow(d));
    }

    isWeekdaySelected(wd: Weekday): boolean {
        return this.inputs.weekdayExclusions.indexOf(wd) >= 0;
    }

    // ---- handlers ---------------------------------------------------------------------

    @DetectMethodChanges()
    onDateKind(kind: 'single' | 'range'): void {
        if (kind === 'single') {
            // Park the range on the way out, bring the parked single date back (owner ruling
            // 2026-09-15: a mode switch never resets a picked value).
            if (!this.isSingle) {
                this._parkedRange = {
                    start: this.rangeStart, end: this.rangeEnd, startIsSeed: this._startIsSeed,
                    weekdayExclusions: this.inputs.weekdayExclusions.slice(),
                    specificDateExclusions: this.inputs.specificDateExclusions.slice(),
                    weekdayOverrides: this.inputs.weekdayOverrides.slice()
                };
            }
            const single = this.isSingle ? this.singleDate : this._parkedSingle;
            this._parkedSingle = null;
            this.inputs = selectSingleDate(this.inputs, single);
            // CR-20260916-005: an EMPTY single date on entering single mode seeds on the anchor
            // (hosts that asked for it). A parked pick came back above and is left alone.
            this.preselectSingle();
        } else {
            // Park the single date on the way out. Entering range mode re-seeds the exclusions
            // from the host's setting, THEN the Start seeds under that gate (ruling 2,
            // CR-20260915-002): first available expiration after the anchor, excluded weekdays not
            // available. A PARKED range comes back instead of a seed: the trader's own Start stays
            // his own (never re-seeded), and the End returns with it. The exclusions still re-seed
            // — that is BR-W3's kind-change rule unless the host opts into remembering them.
            if (this.isSingle) {
                this._parkedSingle = this.singleDate;
            }
            const parked = this._parkedRange;
            this._parkedRange = null;
            const start = parked ? parked.start : this.rangeStart;
            const end = parked ? parked.end : this.rangeEnd;
            this.inputs = selectRange(this.inputs, start, end, this._defaultWeekdayExclusions);
            if (this.rememberRangeExclusions && parked) {
                this.inputs.weekdayExclusions = parked.weekdayExclusions.slice();
                this.inputs.specificDateExclusions = parked.specificDateExclusions.slice();
                this.inputs.weekdayOverrides = parked.weekdayOverrides.slice();
            }
            this._startIsSeed = parked ? (parked.startIsSeed && !!start) : this._startIsSeed;
            this.preselectStart();
        }
        this.calendarOpen = false;
        this.emit();
    }

    @DetectMethodChanges()
    onSingleDate(e: { value?: Date }): void {
        const next = (e && e.value) || null;
        // dx-date-box raises onValueChanged for the programmatic seed write too; an echo of the
        // day already held is not a pick (same guard as onRangeStart).
        if (this.sameDay(next, this.singleDate)) {
            return;
        }
        this.inputs = selectSingleDate(this.inputs, next);
        this._singleIsSeed = false;
        this.emit();
    }

    // CR-20260916-005: the single-date twin of preselectStart, for hosts with
    // singleDateFollowsAnchor. Seeds an EMPTY (or still-seeded) single date with the first
    // selectable listed day STRICTLY AFTER the anchor — the same rule as the range Start. Not the
    // anchor day itself: when the anchor is today, a roll ON today has zero days out and the
    // ORION drops it before pricing (owner smoke 2026-09-16: seeded on the cursor day, zero
    // cards, no reason on screen). No-op when: not asked; not in single mode; a trader's pick
    // exists; no anchor; nothing selectable. Does NOT emit — returns true when it changed the
    // date, and the caller decides.
    private preselectSingle(): boolean {
        if (!this.singleDateFollowsAnchor || !this.isSingle) {
            return false;
        }
        if (this.isValid(this.singleDate) && !this._singleIsSeed) {
            return false;
        }
        this.refreshAnchor();
        if (!this._anchorKey) {
            return false;
        }
        const seed = nextSelectableDayAfter(this._anchorKey, this.disabledDatesFn);
        if (!seed || this.sameDay(seed, this.singleDate)) {
            return false;
        }
        this.inputs = selectSingleDate(this.inputs, seed);
        this._singleIsSeed = true;
        return true;
    }

    // CR-20260904-001 (BR-W5 - REVERSES this CR's own Q2 ruling / IV-6, owner-confirmed 2026-09-06): picking
    // a range boundary NO LONGER touches the exclusions. The old recordMondayPick un-excluded a
    // picked Monday, which made one gesture do two things — move the boundary AND silently drop the
    // exclusion — with no way to put it back from this widget. Excluded days are now simply not
    // selectable here; the exclusion calendar is the one place that brings them back.
    @DetectMethodChanges()
    onRangeStart(e: { value?: Date }): void {
        const next = (e && e.value) || null;
        // dx-date-box raises onValueChanged for PROGRAMMATIC [value] writes too (the seed). A write
        // that lands on the day already held is an echo, not a pick: ignore it, or the seed would
        // be re-labelled as the trader's own choice and the chain-arrival re-seed would refuse it.
        if (this.sameDay(next, this.rangeStart)) {
            return;
        }
        this.writeStart(next, false);
        this.calendarOpen = false;   // range changed — reopen the calendar to re-shade
        this.emit();
    }

    // The ONE place a Start is written. Forward-only End (owner, 2026-09-08): an End that is not
    // strictly after the new Start is no longer legal, and clearing the Start leaves nothing for the
    // End to be after — drop it. isSeed records whether the value is ORION's placement or the trader's.
    private writeStart(next: Date | null, isSeed: boolean): void {
        const end = this.rangeEnd;
        const endStillLegal = !!next && this.isValid(end) && (end as Date).getTime() > next.getTime();
        this.inputs = selectRange(this.inputs, next, endStillLegal ? end : null);
        this._startIsSeed = isSeed && !!next;
    }

    // Owner ruling 2026-09-08 (screencast 16-59-17): the shorts were edited while ORION is open.
    // Re-read the anchor, then: a Start (seed OR pick) that now lies BEFORE the anchor is not a legal
    // roll date — replace it with the seed; an empty Start seeds; a Start on or after the anchor is
    // untouched (moving the shorts backward only makes more days selectable). Called by the
    // orchestrator on the host's positions-changed event.
    onAnchorChanged(): void {
        if (this.isSingle) {
            const wasKey = this._anchorKey;
            this.refreshAnchor();
            // CR-20260916-005: a single date that follows the anchor rolls forward with it — a
            // date (seed OR pick) now BEFORE the anchor is not legal, replace it with the seed; an
            // empty one seeds. A date on or after the anchor is untouched.
            if (this.singleDateFollowsAnchor && this._anchorKey !== wasKey) {
                const min = this.singleDateMin;
                const single = this.singleDate;
                let moved = false;
                if (this.isValid(single) && min && (single as Date).getTime() < min.getTime()) {
                    this._singleIsSeed = true;   // let preselectSingle re-place it
                    moved = this.preselectSingle();
                } else {
                    moved = this.preselectSingle();
                }
                this._changeDetector.markForCheck();
                if (moved) {
                    this.emit();
                }
            }
            return;
        }
        const before = this._anchorKey;
        this.refreshAnchor();
        if (this._anchorKey === before) {
            return;
        }
        let changed = false;
        const start = this.rangeStart;
        const min = this.rangeStartMin;
        if (this.isValid(start) && min && (start as Date).getTime() < min.getTime()) {
            this.rebuildRangeDisabledDates();
            const seed = nextSelectableDayAfter(this._anchorKey as string, this.rangeDisabledDatesFn);
            this.writeStart(seed, true);   // a pick that became illegal is rolled too (owner ruling)
            changed = true;
        } else if (this.preselectStart()) {
            changed = true;
        }
        this._changeDetector.markForCheck();   // [min] moved even when the Start did not
        if (changed) {
            this.calendarOpen = false;
            this.emit();
        }
    }

    private refreshAnchor(): void {
        this._anchorKey = this.anchorKeyReader ? this.anchorKeyReader() : null;
    }

    // Owner request 2026-09-08: the range tab presented with an EMPTY Start -> Start = the next day the
    // Start picker itself would accept, strictly after the ANCHOR (the earlier short-option
    // expiration, or today when that is past — owner ruling the same day). No-op when: not in range
    // mode; a trader-picked Start exists; there is no anchor; nothing selectable is found.
    // A seed (not a pick) MAY be re-placed — that is how a chain arriving late corrects a
    // weekend-only-fallback seed. Does NOT emit — returns true when it changed the Start, and the
    // caller decides whether to emit.
    private preselectStart(): boolean {
        if (this.isSingle) {
            return false;
        }
        if (this.isValid(this.rangeStart) && !this._startIsSeed) {
            return false;
        }
        this.refreshAnchor();
        if (!this._anchorKey) {
            return false;
        }
        this.rebuildRangeDisabledDates();   // the seed must obey the CURRENT gate (weekday rule etc.)
        const seed = nextSelectableDayAfter(this._anchorKey, this.rangeDisabledDatesFn);
        if (!seed || this.sameDay(seed, this.rangeStart)) {
            return false;
        }
        this.writeStart(seed, true);
        return true;
    }

    private keyToLocalDate(key: string): Date | undefined {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key || '');
        if (!m) {
            return undefined;
        }
        return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    }

    private sameDay(a: Date | null | undefined, b: Date | null | undefined): boolean {
        if (!this.isValid(a) || !this.isValid(b)) {
            return !this.isValid(a) && !this.isValid(b);
        }
        const x = a as Date;
        const y = b as Date;
        return x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
    }

    @DetectMethodChanges()
    onRangeEnd(e: { value?: Date }): void {
        this.inputs = selectRange(this.inputs, this.rangeStart, (e && e.value) || null);
        this.calendarOpen = false;
        this.emit();
    }

    @DetectMethodChanges()
    onWeekday(wd: Weekday): void {
        if (this.isSingle) {
            return;   // blocked out on single date
        }
        this.inputs = toggleWeekdayExclusion(this.inputs, wd);
        this.emit();
    }

    @DetectMethodChanges()
    openCalendar(): void {
        if (this.isSingle) {
            return;
        }
        this.calendarOpen = true;
    }

    // One Apply commits BOTH draft sets together — the specific-date carve-outs and the weekday
    // overrides — so the calendar's Cancel really discards everything the trader did in it.
    @DetectMethodChanges()
    onExclusionsApply(result: ExclusionCalendarResult): void {
        this.inputs = {
            date: this.inputs.date,
            weekdayExclusions: this.inputs.weekdayExclusions,
            specificDateExclusions: (result && result.specificDates) || [],
            weekdayOverrides: (result && result.weekdayOverrides) || [],
            bias: this.inputs.bias
        };
        this.calendarOpen = false;
        this.emit();
    }

    @DetectMethodChanges()
    onExclusionsCancel(): void {
        this.calendarOpen = false;
    }

    @DetectMethodChanges()
    onBiasYes(): void {
        this.inputs = setBiasEnabled(this.inputs, true);
        this.emit();
    }

    @DetectMethodChanges()
    onBiasNo(): void {
        this.inputs = setBiasEnabled(this.inputs, false);
        this.emit();
    }

    @DetectMethodChanges()
    onBiasDir(dir: BiasDirection): void {
        this.inputs = setBiasDirection(this.inputs, dir);
        this.emit();
    }

    // ---- dx-button-group click adapters (delegate to the decorated reducers above; CD fires there) ----

    onDateKindClick(e: { itemData: { id: 'single' | 'range' } }): void {
        this.onDateKind(e.itemData.id);
    }

    onWeekdayClick(e: { itemData: { id: Weekday } }): void {
        this.onWeekday(e.itemData.id);
    }

    onBiasClick(e: { itemData: { id: 'yes' | 'no' } }): void {
        if (e.itemData.id === 'yes') {
            this.onBiasYes();
        } else {
            this.onBiasNo();
        }
    }

    onBiasDirClick(e: { itemData: { id: BiasDirection } }): void {
        this.onBiasDir(e.itemData.id);
    }

    // Public hook so the orchestrator can reopen a fieldset from a breadcrumb click.
    focusExclusions(): void {
        if (!this.isSingle) {
            this.calendarOpen = true;
            this._changeDetector.markForCheck();
        }
    }

    // CR-20260904-001 (BR-W1): the inline reason the Run button is off. Null while the range is
    // legal, so the row disappears the moment the trader fixes it. Quotes the configured cap.
    get rangeCapMessage(): string | null {
        return exceedsRangeCap(this.inputs, { maxRangeDays: this._maxRangeDays })
            ? rangeCapMessage(this._maxRangeDays as number)
            : null;
    }

    // CR-20260904-001 (BR-W5): the hint pointing at the one place an excluded day can be brought
    // back. Same condition as the boundary gate itself, so the two can never disagree: it appears
    // exactly when there ARE weekdays the rule is holding back. Off on a single date (no
    // exclusions there at all) and off once no weekday button is on (nothing left to bring back).
    get showWeekdayOverrideHint(): boolean {
        return !this.isSingle && this.inputs.weekdayExclusions.length > 0;
    }

    private emit(): void {
        this.rebuildRangeDisabledDates();
        this.stateChange.emit({
            inputs: this.inputs,
            valid: isRollComplete(this.inputs, { maxRangeDays: this._maxRangeDays })
        });
    }

    // Rebuild the boundary predicate ONLY when what it gates actually changes. Cause -> effect:
    // handing [disabledDates] a fresh function on every emit would make DevExtreme re-apply the
    // option mid-interaction and reset the open dropdown under the trader's cursor.
    private rebuildRangeDisabledDates(): void {
        // The weekday gate holds only while the rule is in force: in range mode, with at least one
        // Day-exclusions button on. Drop it otherwise, or the picker would refuse a day that
        // nothing excludes. CR-20260915-002: EVERY excluded weekday gates, not only Monday.
        const excluded = this.isSingle ? [] : this.inputs.weekdayExclusions;
        const ruleOn = excluded.length > 0;
        const keys = ruleOn ? weekdayOverrideKeys(this.inputs) : [];
        const signature = ruleOn ? excluded.join('|') + ':' + keys.join(',') : 'off';
        if (signature === this._rangeDisabledSignature) {
            return;
        }
        this._rangeDisabledSignature = signature;
        this.rangeDisabledDatesFn = makeRangeBoundaryDisabledDates(
            this._availableExpirations, excluded, keys
        );
    }

    private isValid(d: Date | null | undefined): boolean {
        return d instanceof Date && !isNaN(d.getTime());
    }

    private fmtDow(d: Date): string {
        const day = d.getDate();
        const dd = day < 10 ? '0' + day : '' + day;
        const yy = d.getFullYear() % 100;
        const yyStr = yy < 10 ? '0' + yy : '' + yy;
        return DOW3[d.getDay()] + ' ' + dd + '-' + MONTHS[d.getMonth()] + '-' + yyStr;
    }
}
